/**
 * Global PWA Installation Handler
 * Captures the beforeinstallprompt event and provides a reliable install mechanism
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Store the deferred prompt globally
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let isInstallable = false;
let isInstalled = false;

// Callbacks for state changes
const listeners: Set<() => void> = new Set();

// Initialize on module load
if (typeof window !== 'undefined') {
  // Check if already installed
  const checkInstalled = () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    isInstalled = isStandalone || isIOSStandalone;
    
    // Also check localStorage
    if (localStorage.getItem('pwa_installed') === 'true') {
      isInstalled = true;
    }
  };
  
  checkInstalled();

  // Listen for the beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    
    // Store the event for later use
    deferredPrompt = e as BeforeInstallPromptEvent;
    isInstallable = true;
    
    console.log('[PWA Installer] Install prompt captured and ready');
    
    // Notify all listeners
    listeners.forEach(cb => cb());
  });

  // Listen for successful installation
  window.addEventListener('appinstalled', () => {
    console.log('[PWA Installer] App installed successfully');
    deferredPrompt = null;
    isInstallable = false;
    isInstalled = true;
    localStorage.setItem('pwa_installed', 'true');
    localStorage.setItem('pwa_install_date', new Date().toISOString());
    
    // Notify all listeners
    listeners.forEach(cb => cb());
  });

  // Listen for display mode changes
  window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
    if (e.matches) {
      isInstalled = true;
      isInstallable = false;
      listeners.forEach(cb => cb());
    }
  });
}

/**
 * Check if PWA installation is available
 */
export const canInstall = (): boolean => {
  return isInstallable && !isInstalled && deferredPrompt !== null;
};

/**
 * Check if already running as PWA
 */
export const isRunningAsPWA = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = (window.navigator as any).standalone === true;
  return isStandalone || isIOSStandalone || isInstalled;
};

/**
 * Check if iOS device
 */
export const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

/**
 * Trigger the PWA install prompt
 * @returns Promise<boolean> - true if user accepted, false otherwise
 */
export const installPWA = async (): Promise<boolean> => {
  // Check if iOS - show instructions
  if (isIOS()) {
    // For iOS, we can't programmatically install, just show instructions
    return false;
  }
  
  // Check if we have the deferred prompt
  if (!deferredPrompt) {
    console.log('[PWA Installer] No deferred prompt available');
    return false;
  }

  try {
    // Show the install prompt
    console.log('[PWA Installer] Showing install prompt...');
    await deferredPrompt.prompt();
    
    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`[PWA Installer] User ${outcome} the install`);
    
    if (outcome === 'accepted') {
      deferredPrompt = null;
      isInstallable = false;
      isInstalled = true;
      
      // Notify listeners
      listeners.forEach(cb => cb());
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[PWA Installer] Error showing install prompt:', error);
    return false;
  }
};

/**
 * Subscribe to install state changes
 */
export const onInstallStateChange = (callback: () => void): (() => void) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

/**
 * Get current install state
 */
export const getInstallState = () => ({
  isInstallable,
  isInstalled,
  canInstall: canInstall(),
  isRunningAsPWA: isRunningAsPWA(),
  isIOS: isIOS(),
  hasDeferredPrompt: deferredPrompt !== null
});

/**
 * Force check for install prompt (useful after page interactions)
 */
export const refreshInstallState = (): void => {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = (window.navigator as any).standalone === true;
  
  if (isStandalone || isIOSStandalone) {
    isInstalled = true;
    isInstallable = false;
  }
  
  listeners.forEach(cb => cb());
};

